import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";

import { useAuthContext } from "../../../../lib/context/Auth/AuthContext";
import { useWorkplacesClient } from "../../../../api/grpc/workplaces/useWorkplacesClient";
import { useWorkplaceRequests } from "../../../../api/graphql/useWorkplaceRequests";
import { useDevicesRequests } from "../../../../api/graphql/devices/useDevicesRequests";
import { useEditDeviceFormik } from "../../../shared/Forms/DeviceForm/lib/useEditDeviceFormik";

import ModalSidebar from "../../../Modals/ModalSidebar/ModalSidebar";
import { DeviceFormFields } from "../../../../api/graphql/workplaces";
import { EditDeviceForm } from "../../../shared/Forms/DeviceForm/EditDeviceForm";
import {
  MAX_DEVICE_TELEMETRY_SENSORS,
  UpdateDeviceVariables,
} from "../../../../api/graphql/devices/devices";
import { DeviceInformation } from "./types";
import { LocationDevice } from "./components/LocationDevice";
import useGrpcQuery from "../../../../lib/hooks/useGrpcQuery";
import {
  GetDeviceInfoRequest,
  GetDeviceInfoResponse,
} from "../../../../api/grpc/workplaces/workplaces";

interface Props {
  isOpen: boolean;
  toggle: () => void;
  data: DeviceFormFields;
}

export const EditDevice = (props: Props) => {
  const { isOpen, toggle, data } = props;

  const [loading, setLoading] = useState(false);

  const { user } = useAuthContext();
  const { getDeviceInfo } = useWorkplacesClient();
  const { updateDevice } = useWorkplaceRequests();
  const {
    disconnectDevice,
    disconnectDeviceWayfinder,
    updateDeviceRoom,
    updateDeviceWayfinder,
  } = useDevicesRequests();
  const isRdxDevice = data.typeOfWorkspace === "room";

  const { data: deviceDetails, error: deviceDetailsError } = useGrpcQuery<
    Omit<GetDeviceInfoRequest, "customerId">,
    GetDeviceInfoResponse
  >({
    method: getDeviceInfo,
    variables: {
      deviceId: data.deviceId || "",
    },
  });

  const shouldUpdateDevice = useMemo(() => {
    if (!data.softwareInfo) {
      return false;
    }

    return !!deviceDetails?.isUpdateRequired;
  }, [data.softwareInfo, deviceDetails?.isUpdateRequired]);

  const locationPathItems = useMemo(() => {
    const path = deviceDetails?.locationPatch?.path || [];

    return [...path]
      .sort((a, b) => a.index - b.index)
      .map(({ name }) => name)
      .filter(Boolean);
  }, [deviceDetails?.locationPatch?.path]);
  /*
    Submit callback calls three mutations.
    disconnectDevice & updateDeviceRoom are executed each in certain case, these two mutations are used to trigger the events sent to RDX.
    updateDevice is used to update the other data for that device.
  */
  const formik = useEditDeviceFormik({
    initialValues: {
      name: data.name,
      description: data.description,
      roomId: data?.roomId,
      wayFinderId: data?.wayFinderId,
      autoUpdate: data.autoUpdate,
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setLoading(true);

      if (!user) {
        return;
      }

      try {
        const variables: UpdateDeviceVariables = {
          id: data.id || "",
          name: values.name,
          tenantId: user.customerid,
          description: values.description,
          autoUpdate: values.autoUpdate,
        };

        // Unpair the device if the new value is set to null
        if (data.wayFinderId && values.wayFinderId === null) {
          await disconnectDeviceWayfinder({
            id: data.id || "",
            wayFinderId: data.wayFinderId,
            unassignedSince: Math.floor(Date.now() / 1000),
            warnedUnassigned: true,
          });
        }

        if (data.roomId && values.roomId === null) {
          await disconnectDevice({
            id: data.id || "",
            roomId: data.roomId,
            unassignedSince: Math.floor(Date.now() / 1000),
            warnedUnassigned: true,
          });
        }

        if (
          (values.roomId !== null && data.roomId !== values.roomId) ||
          (values.wayFinderId !== null &&
            data.wayFinderId !== values.wayFinderId)
        ) {
          // Update the device room or wayfinder if the new value is different from the initial
          if (!!values.wayFinderId) {
            await updateDeviceWayfinder({
              id: data.id || "",
              wayFinderId: values.wayFinderId || "",
              oldWayfinderId: data.wayFinderId,
              tenantId: user.customerid,
              unassignedSince: 0,
              warnedUnassigned: false,
            });
          } else {
            await updateDeviceRoom({
              id: data.id || "",
              roomId: values.roomId || "",
              oldRoomId: data.roomId,
              tenantId: user.customerid,
              unassignedSince: 0,
              warnedUnassigned: false,
            });
          }
        }

        await updateDevice(variables);

        toggle();
        toast.success("Device edited successfully!");
      } catch (error: any) {
        toast.error(
          error?.message ||
            "Device couldn't be updated, please reload and try again!"
        );
        setLoading(false);
        console.error(error);
      }
    },
  });

  const deviceInfo: DeviceInformation | null = useMemo(() => {
    //if device unassigned return null
    if (!data.roomId && !data.wayFinderId) {
      return null;
    }

    if (!!data.hardwareInfo && !!data.softwareInfo) {
      const {
        softwareInfo: { androidOS, app },
        hardwareInfo: {
          brand,
          model,
          serialNumber,
          resultion,
          dpi,
          ip,
          mac,
          firmwareVersion,
          // lastPing,
        },
        telemetrySensors,
      } = data;
      return {
        softwareInfo: {
          "Android OS": androidOS,
          App: app,
        },
        hardwareInfo: {
          Brand: brand,
          Model: model,
          "Serial number": serialNumber,
          "Build number": firmwareVersion,
          "Resolution & DPI": resultion + "/" + dpi,
          "IP address": ip,
          "MAC address": mac,
          // "Last ping": !!lastPing
          //   ? format(new Date(lastPing), "yyyy-MM-dd HH:mm")
          //   : "",
        },
        sensorInfo: isRdxDevice
          ? (telemetrySensors || [])
              .slice(0, MAX_DEVICE_TELEMETRY_SENSORS)
              .map((sensor) => ({
                "Sensor ID": sensor.sensorId || "-",
                "Sensor MAC": sensor.mac || "-",
                Status: sensor.connectionState || "-",
                "Battery level": sensor.sensorBatteryLevel ?? 0,
              }))
          : [],
        sensorLastSync: telemetrySensors?.[0]?.lastSync ?? null,
      };
    } else {
      return null;
    }
  }, [data, isRdxDevice]);

  const workspaceId = useMemo(() => {
    if (!!!data.wayFinderId && !!!data.roomId) {
      return "Unassigned";
    }

    return !!data.roomId ? data.roomId : data.wayFinderId;
  }, [data]);

  return (
    <ModalSidebar
      isOpen={isOpen}
      loading={loading}
      onToggle={toggle}
      title={data.name}
      headerDescription={
        <LocationDevice
          hasLocation={!!data.roomId || !!data.wayFinderId}
          locationPathItems={locationPathItems}
          locationError={!!deviceDetailsError}
        />
      }
      onSubmit={formik.handleSubmit}
    >
      <EditDeviceForm
        id={data.id || ""}
        formik={formik}
        isWayfinderDevice={!isRdxDevice}
        deviceInfo={deviceInfo}
        deviceId={data.deviceId || ""}
        workspaceId={workspaceId}
        shouldUpdateDevice={shouldUpdateDevice}
        appVersion={data.softwareInfo?.version}
        isOnline={data.isOnline}
        lastDevicePing={data.lastDevicePing}
      />
    </ModalSidebar>
  );
};
